import { FC } from "react";
import { Grid, Form } from "antd";
import type { FormInstance } from "antd";
import { withConditionalComponent } from "src/HOC/WithMobileComponent";
import { MOBILE_BREAKPOINT_KEY } from "src/constants";
import type { GuestsInputType } from "src/Modules/Hotels/constants";
import GuestTabsWeb from "./layouts/GuestTabs.web";
import GuestTabsMob from "./layouts/GuestTabs.mob";
import type { Guest } from "../../utils";

export interface GuestTabItem {
  value: string;
  name: string;
  type: Guest["type"];
  roomIndex: number;
  isLead: boolean;
  isComplete: boolean;
  hasError: boolean;
}

export interface GuestTabsRenderProps {
  items: GuestTabItem[];
  value: string;
  onChange: (value: string) => void;
}

const GuestTabsConditional = withConditionalComponent(GuestTabsWeb, GuestTabsMob);

export interface GuestTabsProps {
  guests: Guest[];
  activeKey: string | null;
  onChange: (key: string) => void;
  form: FormInstance;
}

const GuestTabs: FC<GuestTabsProps> = ({ guests, activeKey, onChange, form }) => {
  const isLargeScreen = !!Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];
  const values: GuestsInputType = Form.useWatch((allValues) => allValues, { form, preserve: true }) ?? {};
  const erroredKeys = new Set(
    form
      .getFieldsError()
      .filter((field) => field.errors.length)
      .map((field) => field.name[0]),
  );

  const items: GuestTabItem[] = guests.map((guest) => {
    const guestValues = values[guest.key] ?? {};
    const isLead = !!guestValues.isLeadGuest;
    const hasContact = !isLead || Boolean(guestValues.contact?.email && guestValues.contact?.phone);
    const hasError = erroredKeys.has(guest.key);
    return {
      value: guest.key,
      name: guestValues.firstName?.trim() || guest.label,
      type: guest.type,
      roomIndex: guest.roomIndex,
      isLead,
      hasError,
      isComplete: !hasError && Boolean(guestValues.title && guestValues.firstName && guestValues.lastName) && hasContact,
    };
  });

  const renderProps: GuestTabsRenderProps = {
    items,
    value: activeKey ?? items[0]?.value ?? "",
    onChange,
  };

  return <GuestTabsConditional trigger={isLargeScreen} props={renderProps} />;
};

export default GuestTabs;
